import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  History as HistoryIcon,
  ArrowBack as BackIcon,
  FactCheckOutlined as BinderVerificationIcon,
} from '@mui/icons-material';
import HarnessLogo from '../../assets/harnessLogo.svg?react';
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from '@taxassist/api-client';

const SIDEBAR_WIDTH = 72; // Same width as main navigation

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean; // Flag for admin-only menu items
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/ops/dash', adminOnly: true },
  { text: 'Jobs', icon: <WorkIcon />, path: '/ops/jobs' },
  { text: 'Binder Verification', icon: <BinderVerificationIcon />, path: '/ops/binder-verification' },
  { text: 'Firms', icon: <BusinessIcon />, path: '/ops/firms' },
  { text: 'Audit Logs', icon: <HistoryIcon />, path: '/ops/audit-logs', adminOnly: true }, // REVIEWER cannot access audit logs
];

export const OpsSidebar: React.FC = ({
  fixed=false,
} : {
  fixed?: boolean;
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleBackToApp = () => {
    navigate('/jobs');
  };

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100vh',
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        position: fixed ? 'fixed' : 'static',
        left: 0,
        top: 0,
        zIndex: theme.zIndex.drawer,
      }}
    >
      {/* Logo/Brand area - Same as main nav but with Ops branding */}
      <Box
        sx={{
          padding: '20px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <HarnessLogo />
      </Box>

      {/* Navigation Items */}
      <List sx={{ flex: 1, p: 1 }}>
        {menuItems
          .filter((item) => {
            // Filter out admin-only items for non-admin users
            if (item.adminOnly && user?.role !== UserRole.admin) {
              return false;
            }
            return true;
          })
          .map((item) => {
            const isActive = location.pathname === item.path;

            return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip title={item.text} placement="right" arrow>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    minHeight: 56,
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: isActive 
                      ? `${theme.palette.info.main}14`
                      : 'transparent',
                    color: isActive 
                      ? theme.palette.info.main
                      : theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: isActive 
                        ? `${theme.palette.info.main}14`
                        : theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: 'center',
                      color: 'inherit',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Back to App Button */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
        <Tooltip title="Back to Main App" placement="right" arrow>
          <ListItemButton
            onClick={handleBackToApp}
            sx={{
              minHeight: 56,
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                justifyContent: 'center',
                color: 'inherit',
              }}
            >
              <BackIcon />
            </ListItemIcon>
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
